import React, { useId } from 'react';
import { getMetaCardTheme, getMetaTier } from '../../utils/metaColor';

// Local a11y-safe colors (tons 800 p/ texto, 500/600 p/ preenchimento >=3:1 sobre o trilho surface-container-high).
const TIER_TEXT = { 1: 'text-red-700', 2: 'text-yellow-800', 3: 'text-emerald-800', 4: 'text-green-800', 5: 'text-green-800' };
const TIER_BADGE = {
  1: 'bg-red-100 text-red-800', 2: 'bg-yellow-100 text-yellow-800', 3: 'bg-emerald-100 text-emerald-800',
  4: 'bg-green-100 text-green-800', 5: 'bg-green-200 text-green-900'
};
const TIER_FILL = { 1: '#ef4444', 2: '#eab308', 3: '#10b981', 4: '#16a34a', 5: '#16a34a' };

const MONTH_NAMES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const MONTH_SHORT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez'
];

// Card enxuto por unidade (relatorio combinado). `goal` traz plannedLeads,
// currentDay/totalDaysInMonth/expectedPacingPct (ritmo congelado no asOf).
function CompactGoalCard({ goal, achieved, state, ariaLabel, accountsOk, accountsFailed }) {
  const titleId = useId();
  const unitName = goal?.unitName || 'Unidade';

  const shell = 'goal-progress break-inside-avoid bg-surface-container-lowest p-4 rounded-2xl border border-outline-variant/30 shadow-sm flex flex-col gap-2';

  if (state === 'loading') {
    return (
      // Decorativo: o bloco pai anuncia o carregamento uma unica vez (role=status)
      <div aria-hidden="true" className={`${shell} animate-pulse motion-reduce:animate-none`}>
        <div className="h-4 bg-surface-container-high rounded w-2/3" />
        <div className="h-6 bg-surface-container-high rounded w-1/2" />
        <div className="h-2.5 bg-surface-container-high rounded w-full" />
      </div>
    );
  }

  const NOTE = {
    no_goal: { icon: 'flag', text: 'Sem meta definida no Funil Projetado' },
    unmapped: { icon: 'link_off', text: 'Unidade sem conta Meta mapeada' },
    forbidden: { icon: 'lock', text: 'Sem acesso a esta unidade' },
    error: { icon: 'info', text: 'Meta indisponível no momento' }
  };

  if (NOTE[state]) {
    return (
      <article aria-label={ariaLabel || `${unitName}: ${NOTE[state].text}`} className={shell}>
        <h4 className="text-sm font-bold text-on-surface line-clamp-2 break-words" title={unitName}>{unitName}</h4>
        <p className="text-xs text-on-surface flex items-center gap-1.5">
          <span className="material-symbols-outlined text-[16px] text-on-surface-variant" aria-hidden="true">{NOTE[state].icon}</span>
          {NOTE[state].text}
        </p>
      </article>
    );
  }

  const target = Number(goal?.plannedLeads || 0);
  const realized = Number(achieved || 0);
  const pct = target > 0 ? (realized / target) * 100 : 0;
  const pctFormatted = pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const barWidth = Math.min(Math.max(pct, 0), 100);
  const tier = getMetaTier(pct);
  const expected = Number(goal?.expectedPacingPct || 0);
  const isAbovePacing = pct >= expected;
  const label = ariaLabel || `${unitName}: ${realized.toLocaleString('pt-BR')} de ${target.toLocaleString('pt-BR')} (${pctFormatted}%)`;

  return (
    <article aria-label={label} className={shell}>
      <h4 id={titleId} className="text-sm font-bold text-on-surface line-clamp-2 break-words" title={unitName}>{unitName}</h4>
      <div>
        <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${TIER_BADGE[tier]}`}>
          {pct >= 100 ? 'Meta batida' : getMetaCardTheme(pct).msg}
        </span>
      </div>
      <div className="flex items-baseline gap-x-2 flex-wrap">
        <span className="text-2xl font-extrabold font-headline text-on-surface">{realized.toLocaleString('pt-BR')}</span>
        <span className="text-xs font-semibold text-on-surface-variant">de {target.toLocaleString('pt-BR')}</span>
        <span className={`text-base font-extrabold font-headline ${TIER_TEXT[tier]}`}>({pctFormatted}%)</span>
      </div>
      <div className="relative">
        <div
          role="progressbar"
          aria-labelledby={titleId}
          aria-valuenow={Math.round(barWidth)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${pctFormatted}% da meta atingida (${realized} de ${target})`}
          className="w-full h-2.5 bg-surface-container-high ring-1 ring-inset ring-outline-variant/40 rounded-full overflow-hidden"
        >
          <div className="h-full rounded-full" style={{ width: `${barWidth}%`, backgroundColor: TIER_FILL[tier] }} />
        </div>
        {expected > 0 && expected < 100 && (
          <div aria-hidden="true" className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${expected}%` }}>
            <div className="w-0.5 h-full bg-on-surface/60 -ml-[1px]" />
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-on-surface font-medium">
        <span className="flex items-center gap-1.5">
          Ritmo {expected}%
          <span className={`inline-block w-2 h-2 rounded-full ${isAbovePacing ? 'bg-emerald-500' : 'bg-amber-500'}`} aria-hidden="true" />
          <strong className="font-bold">{isAbovePacing ? 'No ritmo' : 'Abaixo'}</strong>
        </span>
      </div>
      {state === 'partial' && (
        <p className="text-xs text-amber-900 bg-amber-100 px-2 py-1 rounded-lg font-bold">
          Dados parciais{accountsOk != null && accountsFailed != null ? ` (${accountsOk}/${accountsOk + accountsFailed} contas)` : ''}: o realizado pode estar subestimado.
        </p>
      )}
    </article>
  );
}

export default function LeadGoalProgress({
  goal,
  achieved = 0,
  state: stateProp,
  conversionType = 'message',
  readOnly = false,
  legend: legendProp,
  variant = 'default', // 'default' | 'summary' | 'compact'
  ariaLabel,
  metricLabel: metricLabelProp,
  sourceLabel: sourceLabelProp,
  accountsOk,
  accountsFailed
}) {
  const titleId = useId();

  // Only message and lead conversion types display the goal card
  if (conversionType !== 'message' && conversionType !== 'lead') {
    return null;
  }

  if (variant === 'compact') {
    return (
      <CompactGoalCard
        goal={goal}
        achieved={achieved}
        state={stateProp || goal?.state || 'unmapped'}
        ariaLabel={ariaLabel}
        accountsOk={accountsOk}
        accountsFailed={accountsFailed}
      />
    );
  }

  // If conversionType is lead and achieved is 0 or less, do not render card (avoid 0% red when no clinic data)
  if (variant === 'default' && conversionType === 'lead' && (!achieved || Number(achieved) <= 0)) {
    return null;
  }

  const state = stateProp || goal?.state || 'unmapped';

  // Do not render anything for unmapped or multiple accounts
  if (state === 'unmapped' || state === 'multiple') {
    return null;
  }

  const isClinicLead = conversionType === 'lead';
  const metricLabel = metricLabelProp || (isClinicLead ? 'Leads das Clínicas' : 'Conversas Iniciadas');
  const sourceLabel = sourceLabelProp || (isClinicLead ? 'Dados das Clínicas' : 'Meta Ads');
  const outerSpacing = variant === 'summary' ? '' : 'mb-8 col-span-full';

  // Determine reference year and month (split string to avoid UTC midnight timezone rollback in BRT)
  const now = new Date();
  let year = now.getFullYear();
  let monthIdx = now.getMonth();

  if (goal?.referenceMonth && typeof goal.referenceMonth === 'string') {
    const parts = goal.referenceMonth.slice(0, 10).split('-');
    if (parts.length >= 2) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (!isNaN(y) && !isNaN(m) && m >= 1 && m <= 12) {
        year = y;
        monthIdx = m - 1;
      }
    }
  } else if (goal?.year && goal?.month) {
    year = Number(goal.year);
    monthIdx = Number(goal.month) - 1;
  }

  const monthName = MONTH_NAMES[monthIdx];
  const monthNameCap = monthName.charAt(0).toUpperCase() + monthName.slice(1);
  const monthShort = MONTH_SHORT[monthIdx];

  const totalDaysInMonth = goal?.totalDaysInMonth ?? new Date(year, monthIdx + 1, 0).getDate();
  // If reference month is current calendar month, use today's day; otherwise use full month
  const isCurrentCalendarMonth = now.getFullYear() === year && now.getMonth() === monthIdx;

  // Use frozen currentDay and expectedPacingPct if provided (e.g. from shared link snapshot)
  const currentDay = goal?.currentDay ?? (isCurrentCalendarMonth ? Math.min(now.getDate(), totalDaysInMonth) : totalDaysInMonth);
  const expectedPacingPct = goal?.expectedPacingPct ?? Number(((currentDay / totalDaysInMonth) * 100).toFixed(1));

  const defaultLegend = `Mês de ${monthName}/${year} (1-${currentDay} ${monthShort}), independente do período selecionado`;
  const legendText = legendProp || defaultLegend;

  // Loading skeleton
  if (state === 'loading') {
    return (
      <div role="status" aria-busy="true" aria-label="Carregando meta de leads" className="goal-progress bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/20 shadow-sm animate-pulse motion-reduce:animate-none mb-8 col-span-full">
        <div className="flex justify-between items-center mb-3">
          <div className="h-4 bg-surface-container-high rounded w-48" />
          <div className="h-4 bg-surface-container-high rounded w-24" />
        </div>
        <div className="h-7 bg-surface-container-high rounded w-36 mb-3" />
        <div className="h-3 bg-surface-container-high rounded w-full mb-3" />
        <div className="h-3 bg-surface-container-high rounded w-64" />
      </div>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <div role="status" className="goal-progress bg-surface-container-low p-4 rounded-xl border border-outline-variant/40 text-xs text-on-surface flex items-center gap-2 mb-8 col-span-full">
        <span className="material-symbols-outlined text-[16px] text-on-surface-variant">info</span>
        <span className="font-medium">Meta indisponível</span>
      </div>
    );
  }

  // No goal configured in funnel
  if (state === 'no_goal') {
    return (
      <div role="status" className="goal-progress bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 shadow-sm mb-8 col-span-full">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-surface-container flex items-center justify-center text-on-surface-variant">
              <span className="material-symbols-outlined text-[20px]">flag</span>
            </div>
            <div>
              <p className="text-sm font-bold text-on-surface">
                {goal?.unitName ? `${goal.unitName} — ` : ''}Sem meta definida no Funil Projetado
              </p>
              <p className="text-xs text-on-surface mt-0.5">
                {legendText}
              </p>
            </div>
          </div>
          {!readOnly && (
            <a
              href="/clinic-data/reports?mode=funnel"
              className="print:hidden text-xs font-bold text-primary hover:underline flex items-center gap-1 bg-primary/10 hover:bg-primary/20 px-3.5 py-1.5 rounded-full transition-colors self-start sm:self-auto"
            >
              <span>Definir no Funil Projetado</span>
              <span className="material-symbols-outlined text-[14px]">open_in_new</span>
            </a>
          )}
        </div>
      </div>
    );
  }

  // State OK: Full goal progress display
  const target = Number(goal?.plannedLeads || 0);
  const realized = Number(achieved || 0);
  const pct = target > 0 ? (realized / target) * 100 : 0;
  const pctFormatted = pct.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const barWidth = Math.min(Math.max(pct, 0), 100);

  const theme = getMetaCardTheme(pct);
  const tier = getMetaTier(pct);
  const hexColor = TIER_FILL[tier];
  const badgeClass = TIER_BADGE[tier];

  const diff = realized - target;
  let remainingText = '';
  let excessText = '';
  if (diff >= 0) {
    if (diff === 0) {
      remainingText = 'Meta batida!';
    } else {
      const excessPct = target > 0 ? ((diff / target) * 100).toFixed(1) : '0';
      excessText = `+${diff.toLocaleString('pt-BR')} (+${excessPct}%) acima`;
      remainingText = 'Meta batida!';
    }
  } else {
    remainingText = `Faltam ${(Math.abs(diff)).toLocaleString('pt-BR')}`;
  }

  const isAbovePacing = pct >= expectedPacingPct;

  return (
    <div className={`goal-progress bg-surface-container-lowest p-5 sm:p-6 rounded-2xl border border-outline-variant/30 shadow-[0_4px_24px_rgba(25,28,30,0.03)] ${outerSpacing}`}>
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <span className="material-symbols-outlined text-[18px]">
              {isClinicLead ? 'local_hospital' : 'ads_click'}
            </span>
          </div>
          <div>
            <h3 id={titleId} className="font-headline font-bold text-sm sm:text-base text-on-surface">
              Meta de {metricLabel} — {monthNameCap}/{year}
            </h3>
            <p className="text-xs font-semibold text-on-surface-variant">
              Fonte: {sourceLabel}
            </p>
          </div>
          {goal?.unitName && (
            <span className="inline-block text-xs font-semibold text-on-surface-variant bg-surface-container px-2.5 py-0.5 rounded-full self-start mt-0.5">
              {goal.unitName}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${badgeClass}`}>
            {diff >= 0 ? 'Meta Batida' : theme.msg}
          </span>
          {excessText ? (
            <span className="text-xs font-extrabold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
              {excessText}
            </span>
          ) : (
            <span className="text-xs font-bold text-on-surface bg-surface-container px-2 py-0.5 rounded-full">
              {remainingText}
            </span>
          )}
        </div>
      </div>

      {/* Numerical Stats */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl sm:text-3xl font-extrabold font-headline text-on-surface">
            {realized.toLocaleString('pt-BR')}
          </span>
          <span className="text-sm font-semibold text-on-surface-variant">
            de {target.toLocaleString('pt-BR')}
          </span>
        </div>
        <span
          className={`text-lg sm:text-xl font-extrabold font-headline ${TIER_TEXT[tier]}`}
        >
          ({pctFormatted}%)
        </span>
      </div>

      {/* Progress Bar Container */}
      <div className="relative mb-2">
        <div
          role="progressbar"
          aria-labelledby={titleId}
          aria-valuenow={Math.round(barWidth)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${pctFormatted}% da meta atingida (${realized} de ${target})`}
          className="w-full h-3 bg-surface-container-high ring-1 ring-inset ring-outline-variant/40 rounded-full overflow-hidden relative"
        >
          <div
            className="h-full rounded-full transition-all duration-500 motion-reduce:transition-none"
            style={{
              width: `${barWidth}%`,
              backgroundColor: hexColor
            }}
          />
        </div>

        {/* Expected Pacing Marker */}
        {expectedPacingPct > 0 && expectedPacingPct < 100 && (
          <div
            aria-hidden="true"
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: `${expectedPacingPct}%` }}
            title={`Ritmo esperado até hoje: ${expectedPacingPct}%`}
          >
            <div className="w-0.5 h-full bg-on-surface/60 -ml-[1px]" />
            <div className="w-1.5 h-1.5 rounded-full bg-on-surface -ml-[2px] -mt-[14px]" />
          </div>
        )}
      </div>

      {/* Bottom Sub-info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs text-on-surface font-medium gap-1">
        <span>{legendText}</span>
        <div className="flex items-center gap-1.5">
          <span>
            Ritmo esperado até hoje: <strong className="font-bold text-on-surface">{expectedPacingPct}%</strong> ({currentDay}/{totalDaysInMonth} dias)
          </span>
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              isAbovePacing ? 'bg-emerald-500' : 'bg-amber-500'
            }`}
            aria-hidden="true"
          />
          <span className="font-bold">{isAbovePacing ? 'No ritmo' : 'Abaixo do ritmo'}</span>
        </div>
      </div>
    </div>
  );
}
